import { useState } from "react";
import { DryRunReport } from "@/features/people/components/dry-run-report";
import { importableRowCount, warningRowCount } from "@/features/people/lib/dry-run-summary";
import type { DryRunResult } from "@/features/people/schemas/import.schema";
import { Button } from "@/shared/components/ui/button";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { ListPagination } from "@/shared/components/ui/table-pagination";
import { BANK_UNVERIFIED_NOTE } from "@/shared/import/bank-statutory-import-fields";
import { cn } from "@/shared/lib/cn";

interface Props {
  dry: DryRunResult;
  pending: boolean;
  /// The confirmed mapping sends bank and statutory columns.
  bankStatutoryMapped?: boolean;
  onBack: () => void;
  onImport: (holdInviteEmails: string[]) => void;
}

/// The checkpoint between mapping and writing. Nothing has been imported yet:
/// the dry run only read the file. Import commits exactly this dry run, and
/// the shore skips the rows listed as errors. Rows with warnings still
/// import, so they never block the button.
export function ImportPreviewStep(props: Props) {
  return <ImportPreviewContent key={props.dry.importId} {...props} />;
}

function ImportPreviewContent({
  dry,
  pending,
  bankStatutoryMapped = false,
  onBack,
  onImport,
}: Props) {
  const importable = importableRowCount(dry);
  const skipped = dry.classifications.error;
  const warned = warningRowCount(dry);
  const newRows = dry.rows.filter((row) => row.classification === "new" && row.email);
  const [held, setHeld] = useState<ReadonlySet<string>>(new Set());
  // reason: this page is transient import-form state, reset with the dry-run id.
  const [pageIndex, setPageIndex] = useState(0);
  const visibleRows = newRows.slice(pageIndex * 50, (pageIndex + 1) * 50);
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h3 className="text-sm font-semibold">Review before importing</h3>
        <p className="text-xs text-text-secondary">
          Nothing has been imported yet. Check what will change, then import.
        </p>
      </div>

      <div className="scrollbar-thin min-h-0 flex-1 overflow-auto pr-1">
        <DryRunReport dry={dry} />
        {newRows.length > 0 ? (
          <fieldset className="mt-3 flex flex-col gap-2">
            <legend className="text-xs font-medium text-text-primary">Hold an invite</legend>
            <p className="text-xs text-text-secondary">
              A held person stays Invited with no code until you resend. Everyone else gets an
              invitation email.
            </p>
            <p className="text-xs text-text-secondary" aria-live="polite">
              {held.size} of 500 invites held. You can hold up to 500 invites per import.
            </p>
            {visibleRows.map((row) => {
              const email = row.email ?? "";
              return (
                <label
                  htmlFor={`hold-${dry.importId}-${row.rowIndex}`}
                  key={row.rowIndex}
                  className="flex min-h-11 items-center gap-2 text-xs"
                >
                  <Checkbox
                    id={`hold-${dry.importId}-${row.rowIndex}`}
                    disabled={pending || (held.size >= 500 && !held.has(email))}
                    checked={held.has(email)}
                    onCheckedChange={(checked) => {
                      setHeld((current) => {
                        const next = new Set(current);
                        if (checked === true) {
                          if (next.size < 500) next.add(email);
                        } else next.delete(email);
                        return next;
                      });
                    }}
                  />
                  Don't invite {email} yet
                </label>
              );
            })}
            <ListPagination
              total={newRows.length}
              pageIndex={pageIndex}
              pageSize={50}
              onPageChange={setPageIndex}
            />
          </fieldset>
        ) : null}
      </div>

      {bankStatutoryMapped && importable > 0 ? (
        <p className="rounded-md border border-border bg-surface-subtle px-3 py-2 text-xs font-medium text-text-primary">
          {BANK_UNVERIFIED_NOTE}
        </p>
      ) : null}

      <div
        className={cn(
          "rounded-md border px-3 py-2 text-xs",
          importable > 0
            ? "border-border bg-surface-subtle text-text-secondary"
            : "border-destructive/40 bg-destructive/10 text-destructive",
        )}
      >
        {importable > 0
          ? `Import will add or update ${importable} ${importable === 1 ? "person" : "people"}${
              warned > 0 ? ` (${warned} with some fields skipped)` : ""
            }${
              skipped > 0 ? ` and skip ${skipped} row${skipped === 1 ? "" : "s"} with errors` : ""
            }. New people get an invitation email.`
          : "There is nothing to import. Go back to fix the mapping, or fix the file and upload it again."}
      </div>

      <div className="flex items-center justify-between gap-2">
        <Button type="button" variant="outline" onClick={onBack} disabled={pending}>
          Back
        </Button>
        <Button
          type="button"
          onClick={() => onImport([...held])}
          disabled={pending || importable === 0}
        >
          {pending ? "Importing..." : `Import ${importable}`}
        </Button>
      </div>
    </div>
  );
}
